import { useMemo } from 'react';

export interface TimeZoneInfo {
  /** IANA time zone name, e.g. "America/New_York" */
  timeZone: string;
  /**
   * Offset from UTC in minutes, using the standard sign convention where
   * zones ahead of UTC are positive (e.g. UTC+2 => 120, UTC-5 => -300).
   * Note: this is the inverse sign of `Date.prototype.getTimezoneOffset`.
   */
  timeZoneOffsetMinutes: number;
  /** Offset from UTC in whole hours (can be fractional for zones like UTC+5:30). */
  timeZoneOffsetHours: number;
  /** Raw value of `Date.prototype.getTimezoneOffset()` (inverted sign). */
  rawOffsetMinutes: number;
  /** Formatted offset string, e.g. "+02:00" or "-05:30". */
  formattedOffset: string;
}

const getTimeZoneName = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

const formatOffset = (offsetMinutes: number): string => {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;

  const pad = (value: number): string => String(value).padStart(2, '0');

  return `${sign}${pad(hours)}:${pad(minutes)}`;
};

/**
 * Returns time zone information derived from the user's browser.
 * `timeZoneOffsetMinutes` is normalized to the standard sign convention
 * (zones ahead of UTC are positive) and can be sent directly to the API.
 */
export const useTimeZone = (date: Date = new Date()): TimeZoneInfo => {
  return useMemo(() => {
    const rawOffsetMinutes = date.getTimezoneOffset();
    // Invert sign so that zones ahead of UTC are positive.
    const timeZoneOffsetMinutes = -rawOffsetMinutes;

    return {
      timeZone: getTimeZoneName(),
      timeZoneOffsetMinutes,
      timeZoneOffsetHours: timeZoneOffsetMinutes / 60,
      rawOffsetMinutes,
      formattedOffset: formatOffset(timeZoneOffsetMinutes),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date.getTime()]);
};
